// RoyethTab.js — Tab 3: RoyETH carry trade backtest using DSV output from Tab 2.
// Compares RoyETH performance against plain wstETH holding.

import { useState, useMemo, useCallback } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ReferenceArea,
} from 'recharts';
import { useSession, useSessionDispatch } from './SessionContext';
import { DEFAULT_STRATEGIES, simulate } from '../../lib/dsv-engine';
import { getNavData, royethRateData } from '../../lib/nav-data-loader';
import { simulateRoyETH } from '../../lib/royeth-engine';
import COLORS from '../../lib/colors';

// ── Formatting helpers ──

const fmtPct = (v, d = 2) => (v * 100).toFixed(d) + '%';
const fmtPctRaw = (v, d = 2) => v.toFixed(d) + '%';
const tickStyle = { fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" };
const chartMargin = { top: 10, right: 30, left: 10, bottom: 10 };

// ── Rate data date range (module-level constant — royethRateData is a static import) ──
const rateDataDates = Object.keys((royethRateData && royethRateData.data) || {}).sort();
const rateDataStart = rateDataDates.length > 0 ? rateDataDates[0] : null;
const rateDataEnd = rateDataDates.length > 0 ? rateDataDates[rateDataDates.length - 1] : null;

// ── Inline UI components (same patterns as MarketsTab / DsvPortfolioTab) ──

const Card = ({ title, children }) => (
  <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.borderLight}`, borderRadius: 4, padding: 16, marginBottom: 12 }}>
    {title && <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary, marginBottom: 12 }}>{title}</div>}
    {children}
  </div>
);

const MetricRow = ({ label, value, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
    <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.textSecondary }}>{label}</span>
    <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 13, fontWeight: 600, color: color || COLORS.ink }}>{value}</span>
  </div>
);

const Slider = ({ label, value, onChange, min, max, step, suffix = '' }) => {
  const decimals = step < 0.01 ? 3 : step < 0.1 ? 2 : step < 1 ? 1 : 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary }}>{label}</span>
        <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 13, fontWeight: 600, color: COLORS.ink }}>
          {typeof value === 'number' ? value.toFixed(decimals) : value}{suffix}
        </span>
      </div>
      <input type="range" className="integrated-slider" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ width: '100%', height: 4, appearance: 'none', WebkitAppearance: 'none', background: `linear-gradient(to right, ${COLORS.accent} 0%, ${COLORS.accent} ${((value - min) / (max - min)) * 100}%, ${COLORS.borderDark} ${((value - min) / (max - min)) * 100}%, ${COLORS.borderDark} 100%)`, borderRadius: 2, outline: 'none', cursor: 'pointer' }} />
    </div>
  );
};

const Toggle = ({ label, value, onChange }) => (
  <div
    onClick={() => onChange(!value)}
    style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
  >
    <div style={{
      width: 36, height: 20, borderRadius: 10,
      background: value ? COLORS.accent : COLORS.borderDark,
      border: `1px solid ${value ? COLORS.accent : COLORS.borderDark}`,
      position: 'relative', transition: 'all 0.2s', flexShrink: 0,
    }}>
      <div style={{
        width: 16, height: 16, borderRadius: 8,
        background: value ? COLORS.bg : COLORS.textTertiary,
        position: 'absolute', top: 1, left: value ? 18 : 1,
        transition: 'left 0.2s',
      }} />
    </div>
    {label && <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.textSecondary }}>{label}</span>}
  </div>
);

const ChartTooltip = ({ active, payload, labelFormatter, valueFormatter }) => {
  if (!active || !payload?.length) return null;
  const fmt = valueFormatter || ((v) => typeof v === 'number' ? v.toFixed(4) : v);
  return (
    <div style={{ background: COLORS.ink, color: COLORS.bg, padding: '8px 12px', borderRadius: 2, fontFamily: "'Fragment Mono', monospace", fontSize: 11, lineHeight: 1.6, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
      {labelFormatter && <div style={{ marginBottom: 2 }}>{labelFormatter(payload[0]?.payload)}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color }}>{p.name}: {fmt(p.value)}</div>
      ))}
    </div>
  );
};

const LegendItem = ({ k, label, desc, color, indent = 0, checked, onToggle }) => (
  <label
    onClick={() => onToggle(k)}
    style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', userSelect: 'none', marginLeft: indent * 20, marginBottom: 2 }}
  >
    <input type="checkbox" checked={checked} onChange={() => {}} style={{ accentColor: color, cursor: 'pointer' }} />
    <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, color: checked ? COLORS.ink : COLORS.textTertiary }}>
      <span style={{ fontWeight: 600 }}>{label}</span>
      <span style={{ fontWeight: 400, marginLeft: 3 }}>({desc})</span>
    </span>
  </label>
);

// ── Main component ──

export default function RoyethTab() {
  const { state } = useSession();
  const dispatch = useSessionDispatch();
  const [dateRangeMode, setDateRangeMode] = useState('custom90');
  const [customStartDate, setCustomStartDate] = useState('2025-11-28');
  const [showNavChart, setShowNavChart] = useState(false);
  const [visibleComponents, setVisibleComponents] = useState({
    totalApy: true, staking: true, supply: true,
    dsvYield: true, borrowRate: true, carry: true,
  });

  // ── Dispatch helpers ──
  const updateRoyeth = useCallback((patch) => {
    dispatch({ type: 'UPDATE_ROYETH', patch });
  }, [dispatch]);

  // ── Compute DSV result (replicate DsvPortfolioTab logic) ──
  const dsvResult = useMemo(() => {
    const strategies = DEFAULT_STRATEGIES.map(defaultStrat => {
      const alloc = state.dsv.allocations[defaultStrat.name];
      // Fall back to DEFAULT_STRATEGIES defaults if Tab 2 hasn't been visited yet
      const base = alloc
        ? { ...defaultStrat, enabled: alloc.enabled, allocation: alloc.allocation }
        : { ...defaultStrat };

      // If linked to Tab 1 and market exists, override params from Tab 1
      if (!alloc?.overrideTab1 && defaultStrat.minCoverage > 0) {
        const market = state.markets[defaultStrat.name];
        if (market) {
          base.minCoverage = market.params.minCoverage;
          base.recoveryDuration = market.params.recoveryDuration;
          base.seniorFee = market.params.stFee;
          base.juniorFee = market.params.jtFee;
          base.yieldShareFee = market.params.ysFee;
          base.finalizedLossThreshold = market.liquidation.finalizedLossThreshold;
          base.selfLiquidationBonus = market.liquidation.selfLiquidationBonus;
        }
      }

      return base;
    });

    // Build navData from nav-data-loader
    const navData = {};
    for (const strat of strategies) {
      if (!strat.enabled) continue;
      const nd = getNavData(strat.name);
      if (nd) {
        navData[strat.name] = { dates: nd.dates, navs: nd.navs };
      }
    }

    const simParams = state.dsv.simParams;
    return simulate({
      strategies,
      navData: Object.keys(navData).length > 0 ? navData : null,
      simParams: {
        reversionDays: simParams.reversionDays,
        liquidationBonusMode: simParams.liquidationBonusMode,
        liquidationRedemptionRate: simParams.liquidationRedemptionRate,
      },
    });
  }, [state.markets, state.dsv]);

  // ── Clamp DSV result to rate data availability (both start and end) ──
  const clampedDsvResult = useMemo(() => {
    if (!dsvResult?.dates?.length || !rateDataStart || !rateDataEnd) return dsvResult;
    const firstIdx = dsvResult.dates.findIndex(d => d >= rateDataStart);
    const lastIdx = dsvResult.dates.findLastIndex(d => d <= rateDataEnd);
    if (firstIdx < 0 || lastIdx < 0 || lastIdx <= firstIdx) return dsvResult;
    if (firstIdx === 0 && lastIdx === dsvResult.dates.length - 1) return dsvResult; // no trimming needed
    const offset = firstIdx;
    const count = lastIdx - firstIdx + 1;
    if (count < 2) return dsvResult;
    const trimmedDates = dsvResult.dates.slice(offset, offset + count);
    // Re-base cumulative returns so the clamped window starts at 0
    const aggBase = dsvResult.aggregate.dsvCumReturn[offset];
    const trimmedStrategies = {};
    for (const [name, s] of Object.entries(dsvResult.strategies)) {
      const stBase = s.seniorCumReturn[offset];
      const uBase = s.underlyingCumReturn[offset];
      trimmedStrategies[name] = {
        ...s,
        seniorCumReturn: s.seniorCumReturn.slice(offset, offset + count).map(v => (1 + v) / (1 + stBase) - 1),
        underlyingCumReturn: s.underlyingCumReturn.slice(offset, offset + count).map(v => (1 + v) / (1 + uBase) - 1),
        jtEffectiveNav: s.jtEffectiveNav.slice(offset, offset + count),
        marketStateHistory: s.marketStateHistory?.slice(offset, offset + count),
      };
    }
    return {
      dates: trimmedDates,
      strategies: trimmedStrategies,
      aggregate: {
        ...dsvResult.aggregate,
        dsvCumReturn: dsvResult.aggregate.dsvCumReturn.slice(offset, offset + count).map(v => (1 + v) / (1 + aggBase) - 1),
      },
    };
  }, [dsvResult]); // rateDataStart/rateDataEnd are module-level constants

  // ── Compute full RoyETH result (on clamped DSV data) ──
  const fullRoyethResult = useMemo(() => {
    if (!clampedDsvResult?.dates?.length || clampedDsvResult.dates.length < 2) return null;
    return simulateRoyETH({
      dsvResult: clampedDsvResult,
      rateData: royethRateData,
      config: {
        aaveLTV: state.royeth.aaveLTV * 100,
        liqThreshold: state.royeth.liqThreshold * 100,
        useHistoricRates: state.royeth.useHistoricRates,
        stakingYieldOverride: state.royeth.stakingYield * 100,
        aaveSupplyOverride: state.royeth.aaveSupply * 100,
        usdcBorrowOverride: state.royeth.usdcBorrow * 100,
        ltvMode: state.royeth.ltvMode,
        maxDsvRedemption: state.royeth.maxDsvRedemption,
        harvestEnabled: state.royeth.harvestEnabled,
        harvestIntervalDays: state.royeth.harvestIntervalDays,
      },
    });
  }, [clampedDsvResult, state.royeth]);

  // ── Post-filter RoyETH result by date range ──
  const royethResult = useMemo(() => {
    if (!fullRoyethResult) return null;
    const raw = fullRoyethResult;

    const trimRoyeth = (result, offset, count) => {
      const days = Math.min(count, result.dates.length - offset);
      if (days < 2) return result;
      const slice = (arr) => arr.slice(offset, offset + days);
      // Re-base indexed series so the window starts at 1.0
      const rebase = (arr) => {
        const base = arr[offset];
        return base > 0 ? slice(arr).map(v => v / base) : slice(arr);
      };
      const totalDays = (Date.parse(result.dates[offset + days - 1]) - Date.parse(result.dates[offset])) / 86400000;
      const lastVsEth = result.royethVsEth[offset + days - 1] / result.royethVsEth[offset];
      const totalReturn = lastVsEth - 1;
      const annualizedReturn = totalDays > 0 ? Math.pow(lastVsEth, 365 / totalDays) - 1 : 0;
      const wstLast = result.wstEthVsEth[offset + days - 1] / result.wstEthVsEth[offset];
      const wstEthReturn = wstLast - 1;
      const vsWstEthTotal = totalReturn - wstEthReturn;
      const lastRoyWst = lastVsEth / wstLast;
      const vsWstEthAnnualized = totalDays > 0 ? Math.pow(lastRoyWst, 365 / totalDays) - 1 : 0;
      const apySlice = result.dailyApy.slice(offset + 1, offset + days);
      const avgApy = apySlice.reduce((s, v) => s + v, 0) / apySlice.length;
      const dsvSlice = result.components.dsvYield.slice(offset + 1, offset + days);
      const avgDsvYield = dsvSlice.reduce((s, v) => s + v, 0) / dsvSlice.length;
      const brSlice = result.components.borrowRate.slice(offset + 1, offset + days);
      const avgBorrowRate = brSlice.reduce((s, v) => s + v, 0) / brSlice.length;

      return {
        dates: slice(result.dates),
        royethNav: rebase(result.royethNav),
        royethVsEth: rebase(result.royethVsEth),
        wstEthVsEth: rebase(result.wstEthVsEth),
        ethIndexed: rebase(result.ethIndexed),
        wstEthIndexed: rebase(result.wstEthIndexed),
        dsvSrIndexed: rebase(result.dsvSrIndexed),
        dailyApy: slice(result.dailyApy),
        components: {
          staking: slice(result.components.staking),
          supply: slice(result.components.supply),
          carry: slice(result.components.carry),
          dsvYield: slice(result.components.dsvYield),
          borrowRate: slice(result.components.borrowRate),
        },
        actualLTV: result.actualLTV ? slice(result.actualLTV) : undefined,
        harvests: result.harvests?.filter(h =>
          h.date >= result.dates[offset] && h.date <= result.dates[offset + days - 1]
        ) || [],
        summary: {
          totalReturn, annualizedReturn, days: totalDays,
          avgApy, avgDsvYield, avgBorrowRate,
          wstEthReturn, vsWstEth: vsWstEthTotal, vsWstEthAnnualized,
        },
      };
    };

    if (dateRangeMode === 'latest90' && raw.dates.length > 91) {
      return trimRoyeth(raw, raw.dates.length - 91, 91);
    }
    if (dateRangeMode === 'custom90' && customStartDate && raw.dates.length > 0) {
      const startIdx = raw.dates.findIndex(d => d >= customStartDate);
      if (startIdx >= 0) {
        return trimRoyeth(raw, startIdx, 91);
      }
    }
    return raw;
  }, [fullRoyethResult, dateRangeMode, customStartDate]);

  // ── Chart data: RoyETH vs wstETH (indexed to ETH) ──
  const vsEthData = useMemo(() => {
    if (!royethResult) return [];
    return royethResult.dates.map((date, i) => ({
      date,
      royethVsEth: royethResult.royethVsEth[i],
      wstEthVsEth: royethResult.wstEthVsEth[i],
      ethIndexed: 1.0,
    }));
  }, [royethResult]);

  // ── Chart data: RoyETH NAV (USD) ──
  const navChartData = useMemo(() => {
    if (!royethResult) return [];
    return royethResult.dates.map((date, i) => ({
      date,
      royethNav: royethResult.royethNav[i],
    }));
  }, [royethResult]);

  // ── Chart data: Trailing 7-Day APY ──
  const apyData = useMemo(() => {
    if (!royethResult || royethResult.dates.length < 8) return [];
    return royethResult.dates.map((date, i) => ({
      date,
      dailyApy: i < 7 ? null : royethResult.dailyApy[i],
    }));
  }, [royethResult]);

  // ── Chart data: Component Breakdown ──
  const componentData = useMemo(() => {
    if (!royethResult) return [];
    return royethResult.dates.map((date, i) => {
      if (i === 0) return null;
      return {
        date,
        totalApy: royethResult.dailyApy[i],
        staking: royethResult.components.staking[i],
        supply: royethResult.components.supply[i],
        dsvYield: royethResult.components.dsvYield[i],
        borrowRate: royethResult.components.borrowRate[i],
        carry: royethResult.components.carry[i],
      };
    }).filter(Boolean);
  }, [royethResult]);

  // ── Chart data: Actual LTV Over Time ──
  const ltvData = useMemo(() => {
    if (!royethResult?.actualLTV) return [];
    return royethResult.dates.map((date, i) => ({
      date,
      actualLTV: royethResult.actualLTV[i],
    }));
  }, [royethResult]);

  const summary = royethResult?.summary;

  // ── Render ──
  return (
    <div style={{ padding: '20px 24px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 20, alignItems: 'start' }}>
        {/* ── Left Panel: Config ── */}
        <div>
          {/* Aave Config */}
          <Card title="Aave Config">
            <Slider
              label="LTV"
              value={Math.round(state.royeth.aaveLTV * 100)}
              onChange={(v) => updateRoyeth({ aaveLTV: v / 100 })}
              min={10} max={75} step={1} suffix="%"
            />
            <Slider
              label="Liquidation Threshold"
              value={Math.round(state.royeth.liqThreshold * 100)}
              onChange={(v) => updateRoyeth({ liqThreshold: v / 100 })}
              min={70} max={99} step={1} suffix="%"
            />
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary }}>LTV Mode</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {['fixed', 'dynamic'].map((mode) => (
                    <button key={mode} onClick={() => updateRoyeth({ ltvMode: mode })} style={{
                      fontFamily: "'Fragment Mono', monospace", fontSize: 11, padding: '3px 10px',
                      background: state.royeth.ltvMode === mode ? COLORS.ink : 'transparent',
                      color: state.royeth.ltvMode === mode ? COLORS.bg : COLORS.textSecondary,
                      border: `1px solid ${state.royeth.ltvMode === mode ? COLORS.ink : COLORS.borderLight}`,
                      borderRadius: 3, cursor: 'pointer', textTransform: 'capitalize',
                    }}>
                      {mode}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {state.royeth.ltvMode === 'dynamic' && (
              <Slider
                label="Max DSV Redemption / Day"
                value={state.royeth.maxDsvRedemption}
                onChange={(v) => updateRoyeth({ maxDsvRedemption: v })}
                min={0} max={20} step={0.5} suffix="%"
              />
            )}
          </Card>

          {/* Rate Source */}
          <Card title="Rate Source">
            <Toggle
              label={state.royeth.useHistoricRates ? 'Historic Rates' : 'Override Rates'}
              value={state.royeth.useHistoricRates}
              onChange={(v) => updateRoyeth({ useHistoricRates: v })}
            />
            {!state.royeth.useHistoricRates && (
              <div style={{ marginTop: 12 }}>
                <Slider
                  label="Staking Yield"
                  value={Math.round(state.royeth.stakingYield * 1000) / 10}
                  onChange={(v) => updateRoyeth({ stakingYield: v / 100 })}
                  min={0} max={8} step={0.1} suffix="%"
                />
                <Slider
                  label="Aave Supply"
                  value={Math.round(state.royeth.aaveSupply * 10000) / 100}
                  onChange={(v) => updateRoyeth({ aaveSupply: v / 100 })}
                  min={0} max={1} step={0.01} suffix="%"
                />
                <Slider
                  label="USDC Borrow"
                  value={Math.round(state.royeth.usdcBorrow * 1000) / 10}
                  onChange={(v) => updateRoyeth({ usdcBorrow: v / 100 })}
                  min={0} max={20} step={0.1} suffix="%"
                />
              </div>
            )}
          </Card>

          {/* Harvest & Compound */}
          <Card title="Harvest & Compound">
            <Toggle
              label="Enable Harvest"
              value={state.royeth.harvestEnabled}
              onChange={(v) => updateRoyeth({ harvestEnabled: v })}
            />
            {state.royeth.harvestEnabled && (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary }}>Harvest Interval</span>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <input
                      type="number"
                      min={1}
                      value={state.royeth.harvestIntervalDays}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (v >= 1) updateRoyeth({ harvestIntervalDays: v });
                      }}
                      style={{
                        width: 48, textAlign: 'right',
                        fontFamily: "'Fragment Mono', monospace", fontSize: 13, fontWeight: 600,
                        color: COLORS.ink, background: COLORS.bg,
                        border: `1px solid ${COLORS.borderLight}`, borderRadius: 3,
                        padding: '2px 6px', outline: 'none',
                      }}
                    />
                    <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.textSecondary }}>days</span>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Data Sources */}
          <Card title="Data Sources">
            {clampedDsvResult?.dates?.length >= 2 ? (
              <>
                <MetricRow label="DSV data" value={`${clampedDsvResult.dates[0]} → ${clampedDsvResult.dates[clampedDsvResult.dates.length - 1]}`} />
                <MetricRow label="Rate data" value={`${rateDataStart} → ${rateDataEnd}`} />
                <MetricRow label="Compatible range" value={`${clampedDsvResult.dates.length} days`} />
              </>
            ) : (
              <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 12, color: COLORS.textTertiary, padding: '8px 0' }}>
                No overlapping data between DSV and rate sources.
              </div>
            )}
          </Card>

          {/* Completion toggle */}
          <button
            onClick={() => dispatch({ type: 'SET_ROYETH_STATUS', status: state.royeth.status === 'complete' ? 'incomplete' : 'complete' })}
            style={{
              width: '100%', padding: '10px 0', fontFamily: "'Fragment Mono', monospace", fontSize: 12,
              letterSpacing: '0.05em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: 4,
              background: state.royeth.status === 'complete' ? COLORS.positiveBg : 'transparent',
              color: state.royeth.status === 'complete' ? COLORS.positive : COLORS.textSecondary,
              border: `1px solid ${state.royeth.status === 'complete' ? COLORS.positiveBorder : COLORS.borderLight}`,
            }}
          >
            {state.royeth.status === 'complete' ? 'Mark Incomplete' : 'Mark Complete'}
          </button>

          {/* Summary Metrics */}
          {summary && (
            <Card title="Summary Metrics">
              <MetricRow
                label="Total Return (vs ETH)"
                value={fmtPct(summary.totalReturn)}
                color={summary.totalReturn >= 0 ? COLORS.positive : COLORS.negative}
              />
              <MetricRow
                label="Annualized Return (vs ETH)"
                value={fmtPct(summary.annualizedReturn)}
                color={summary.annualizedReturn >= 0 ? COLORS.positive : COLORS.negative}
              />
              <MetricRow
                label="vs wstETH (total)"
                value={fmtPct(summary.vsWstEth)}
                color={summary.vsWstEth >= 0 ? COLORS.positive : COLORS.negative}
              />
              <MetricRow
                label="vs wstETH (annualized)"
                value={fmtPct(summary.vsWstEthAnnualized)}
                color={summary.vsWstEthAnnualized >= 0 ? COLORS.positive : COLORS.negative}
              />
              <MetricRow
                label="Average APY"
                value={fmtPctRaw(summary.avgApy)}
                color={COLORS.accent}
              />
              <MetricRow
                label="Average DSV Yield"
                value={fmtPctRaw(summary.avgDsvYield)}
              />
              <MetricRow
                label="Average Borrow Rate"
                value={fmtPctRaw(summary.avgBorrowRate)}
                color={COLORS.negative}
              />
              <MetricRow
                label="Period"
                value={`${Math.round(summary.days)} days`}
              />
              {royethResult.harvests?.length > 0 && (
                <>
                  <MetricRow
                    label="Harvests"
                    value={`${royethResult.harvests.length}`}
                  />
                  <MetricRow
                    label="Total wstETH compounded"
                    value={`+${royethResult.harvests.reduce((s, h) => s + h.wstEthBought, 0).toFixed(4)}`}
                    color={COLORS.positive}
                  />
                </>
              )}
            </Card>
          )}
        </div>

        {/* ── Right Panel: Charts ── */}
        <div>
          {/* Date range selector */}
          <Card title="Date Range">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px', background: COLORS.surfaceAlt, border: `1px solid ${COLORS.borderLight}`, borderRadius: 4 }}>
                {[
                  { key: 'full', label: 'Max compatible' },
                  { key: 'latest90', label: 'Latest 90d' },
                  { key: 'custom90', label: '90d from' },
                ].map(({ key, label }) => (
                  <button key={key} onClick={() => setDateRangeMode(key)} style={{
                    fontFamily: "'Fragment Mono', monospace", fontSize: 11, padding: '4px 10px',
                    background: dateRangeMode === key ? COLORS.ink : 'transparent',
                    color: dateRangeMode === key ? COLORS.bg : COLORS.textSecondary,
                    border: 'none', borderRadius: 3, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>
                    {label}
                  </button>
                ))}
                {dateRangeMode === 'custom90' && (
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    style={{
                      fontFamily: "'Fragment Mono', monospace", fontSize: 11, padding: '3px 6px',
                      background: COLORS.bg, color: COLORS.ink, border: `1px solid ${COLORS.borderLight}`,
                      borderRadius: 3, outline: 'none', marginLeft: 2,
                    }}
                  />
                )}
              </div>
              {royethResult?.dates?.length > 0 && (
                <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.textTertiary }}>
                  {royethResult.dates[0]} → {royethResult.dates[royethResult.dates.length - 1]} ({Math.round(royethResult.summary?.days || 0)}d)
                </span>
              )}
            </div>
          </Card>

          {!royethResult ? (
            <Card>
              <div style={{ padding: 24, textAlign: 'center', color: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace", fontSize: 12 }}>
                {clampedDsvResult?.dates?.length >= 2
                  ? 'Computing RoyETH backtest...'
                  : 'No overlapping data between DSV portfolio and rate sources.'}
              </div>
            </Card>
          ) : (
            <>
              {/* 1. RoyETH vs wstETH (indexed to ETH) */}
              <Card title="RoyETH vs wstETH (indexed to ETH)">
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={vsEthData} margin={chartMargin}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                    <XAxis
                      dataKey="date" tick={tickStyle}
                      tickFormatter={(d) => d.slice(5)}
                      interval={Math.max(1, Math.floor(vsEthData.length / 12))}
                    />
                    <YAxis
                      tickFormatter={(v) => v.toFixed(4)}
                      tick={tickStyle} width={60}
                      domain={['auto', 'auto']}
                    />
                    <Tooltip content={<ChartTooltip labelFormatter={(d) => d.date} valueFormatter={(v) => v.toFixed(6)} />} />
                    <Line type="monotone" dataKey="ethIndexed" stroke={COLORS.ethBlue} strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="ETH = 1.0" />
                    <Line type="monotone" dataKey="wstEthVsEth" stroke={COLORS.lidoBlue} strokeWidth={1.5} dot={false} name="wstETH vs ETH" />
                    <Line type="monotone" dataKey="royethVsEth" stroke={COLORS.accent} strokeWidth={2} dot={false} name="RoyETH vs ETH" />
                  </LineChart>
                </ResponsiveContainer>
              </Card>

              {/* 2. RoyETH vs USD (collapsible) */}
              <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.borderLight}`, borderRadius: 4, marginBottom: 12 }}>
                <div
                  onClick={() => setShowNavChart(!showNavChart)}
                  style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}
                >
                  <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary }}>RoyETH vs USD</span>
                  <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.textTertiary }}>{showNavChart ? '▾' : '▸'}</span>
                </div>
                {showNavChart && (
                  <div style={{ padding: '0 16px 16px' }}>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={navChartData} margin={chartMargin}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                        <XAxis
                          dataKey="date" tick={tickStyle}
                          tickFormatter={(d) => d.slice(5)}
                          interval={Math.max(1, Math.floor(navChartData.length / 12))}
                        />
                        <YAxis
                          tickFormatter={(v) => v.toFixed(3)}
                          tick={tickStyle} width={60}
                          domain={['auto', 'auto']}
                        />
                        <Tooltip content={<ChartTooltip labelFormatter={(d) => d.date} valueFormatter={(v) => v.toFixed(6)} />} />
                        <Line type="monotone" dataKey="royethNav" stroke={COLORS.accent} strokeWidth={2} dot={false} name="RoyETH vs USD" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* 3. Trailing 7-Day APY */}
              <Card title="Trailing 7-Day APY">
                {apyData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={apyData} margin={chartMargin}>
                      <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                      <XAxis
                        dataKey="date" tick={tickStyle}
                        tickFormatter={(d) => d.slice(5)}
                        interval={Math.max(1, Math.floor(apyData.length / 12))}
                      />
                      <YAxis
                        tickFormatter={(v) => v.toFixed(1) + '%'}
                        tick={tickStyle} width={60}
                      />
                      <Tooltip content={<ChartTooltip labelFormatter={(d) => d.date} valueFormatter={(v) => v != null ? v.toFixed(2) + '%' : '—'} />} />
                      {apyData.length >= 7 && (
                        <ReferenceArea
                          x1={apyData[0].date} x2={apyData[6].date}
                          fill={COLORS.borderLight} fillOpacity={0.5}
                          label={({ viewBox }) => {
                            const cx = viewBox.x + viewBox.width / 2;
                            const cy = viewBox.y + viewBox.height / 2;
                            return (
                              <text x={cx} y={cy} textAnchor="middle" fill={COLORS.textTertiary} fontSize={9} fontFamily="'Fragment Mono', monospace">
                                <tspan x={cx} dy="-0.5em">initial</tspan>
                                <tspan x={cx} dy="1.2em">averaging</tspan>
                              </text>
                            );
                          }}
                        />
                      )}
                      <Line type="monotone" dataKey="dailyApy" stroke={COLORS.accent} strokeWidth={1.5} dot={false} name="Daily APY" connectNulls={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: 40, color: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace", fontSize: 12 }}>
                    Not enough data for trailing APY (need 8+ days).
                  </div>
                )}
              </Card>

              {/* 4. Component Breakdown */}
              <Card title="Component Breakdown (annualized daily %)">
                {componentData.length > 0 ? (
                  <>
                    {/* Interactive legend */}
                    {(() => {
                      const toggleComponent = (k) => setVisibleComponents(prev => ({ ...prev, [k]: !prev[k] }));
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 12 }}>
                          <LegendItem k="totalApy" label="Total APY" desc="staking + supply + carry" color={COLORS.accent} checked={visibleComponents.totalApy} onToggle={toggleComponent} />
                          <LegendItem k="staking" label="Staking" desc="wstETH staking yield" color={COLORS.lidoBlue} indent={1} checked={visibleComponents.staking} onToggle={toggleComponent} />
                          <LegendItem k="supply" label="Supply" desc="Aave collateral APY" color={COLORS.textTertiary} indent={1} checked={visibleComponents.supply} onToggle={toggleComponent} />
                          <LegendItem k="carry" label="Carry" desc="net spread × LTV" color={COLORS.positive} indent={1} checked={visibleComponents.carry} onToggle={toggleComponent} />
                          <LegendItem k="dsvYield" label="DSV Yield" desc="raw senior yield" color={'#E8A838'} indent={2} checked={visibleComponents.dsvYield} onToggle={toggleComponent} />
                          <LegendItem k="borrowRate" label="Borrow Rate" desc="USDC borrow cost" color={COLORS.negative} indent={2} checked={visibleComponents.borrowRate} onToggle={toggleComponent} />
                        </div>
                      );
                    })()}
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={componentData} margin={chartMargin}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                        <XAxis
                          dataKey="date" tick={tickStyle}
                          tickFormatter={(d) => d.slice(5)}
                          interval={Math.max(1, Math.floor(componentData.length / 12))}
                        />
                        <YAxis
                          tickFormatter={(v) => v.toFixed(1) + '%'}
                          tick={tickStyle} width={60}
                        />
                        <Tooltip content={<ChartTooltip labelFormatter={(d) => d.date} valueFormatter={(v) => v.toFixed(2) + '%'} />} />
                        {visibleComponents.totalApy && <Line type="monotone" dataKey="totalApy" stroke={COLORS.accent} strokeWidth={2.5} dot={false} name="Total APY" />}
                        {visibleComponents.staking && <Line type="monotone" dataKey="staking" stroke={COLORS.lidoBlue} strokeWidth={1.2} dot={false} name="Staking" />}
                        {visibleComponents.supply && <Line type="monotone" dataKey="supply" stroke={COLORS.textTertiary} strokeWidth={1.2} dot={false} name="Supply" />}
                        {visibleComponents.dsvYield && <Line type="monotone" dataKey="dsvYield" stroke={'#E8A838'} strokeWidth={1.2} dot={false} name="DSV Yield" />}
                        {visibleComponents.borrowRate && <Line type="monotone" dataKey="borrowRate" stroke={COLORS.negative} strokeWidth={1.2} dot={false} name="Borrow Rate" />}
                        {visibleComponents.carry && <Line type="monotone" dataKey="carry" stroke={COLORS.positive} strokeWidth={1.8} dot={false} name="Carry" />}
                      </LineChart>
                    </ResponsiveContainer>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: 40, color: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace", fontSize: 12 }}>
                    No component data available.
                  </div>
                )}
              </Card>

              {/* 5. Actual LTV Over Time (only for dynamic mode) */}
              {ltvData.length > 0 && (
                <Card title="Actual LTV Over Time">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={ltvData} margin={chartMargin}>
                      <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                      <XAxis
                        dataKey="date" tick={tickStyle}
                        tickFormatter={(d) => d.slice(5)}
                        interval={Math.max(1, Math.floor(ltvData.length / 12))}
                      />
                      <YAxis
                        tickFormatter={(v) => v.toFixed(0) + '%'}
                        tick={tickStyle} width={60}
                        domain={[0, 100]}
                      />
                      <Tooltip content={<ChartTooltip labelFormatter={(d) => d.date} valueFormatter={(v) => v.toFixed(2) + '%'} />} />
                      <ReferenceLine y={state.royeth.liqThreshold * 100} stroke={COLORS.negative} strokeDasharray="4 3" label={{ value: 'Liq. Threshold', fill: COLORS.negative, fontSize: 10, fontFamily: "'Fragment Mono', monospace" }} />
                      <Line type="monotone" dataKey="actualLTV" stroke={COLORS.accent} strokeWidth={1.5} dot={false} name="Actual LTV" />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              )}

            </>
          )}
        </div>
      </div>
    </div>
  );
}
